import React, { useState, useEffect } from 'react';

const EmailAssistant = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [emails, setEmails] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [processedEmails, setProcessedEmails] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastSync, setLastSync] = useState(null);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState({
    phoneNumberId: process.env.REACT_APP_WHATSAPP_PHONE_ID || '723084320894424',
    accessToken: process.env.REACT_APP_WHATSAPP_ACCESS_TOKEN || 'EAAIoZBIH3PQoBPAZCpUtL3C7qzF67EyKM3LOC',
    assistantNumber: process.env.REACT_APP_ASSISTANT_NUMBER || '+6596927902'
  });
  const [testMode, setTestMode] = useState(true);

  // Auto-setup WhatsApp on component mount
  useEffect(() => {
    if (whatsappConfig.phoneNumberId && whatsappConfig.accessToken && whatsappConfig.assistantNumber) {
      setWhatsappConnected(true);
    }
  }, [whatsappConfig]);

  // Production Gmail connection using Google Identity Services
  const connectToGmail = async () => {
    setConnectionStatus('connecting');
    
    try {
      console.log('🔗 Connecting to Gmail API using Google Identity Services...');
      await loadGoogleIdentityServices();
      await initializeGoogleIdentityAuth();
      
    } catch (error) {
      console.error('Gmail connection error:', error);
      setConnectionStatus('error');
      alert('Gmail connection failed: ' + error.message);
    }
  };

  const loadGoogleIdentityServices = () => {
    return new Promise((resolve, reject) => {
      // Check if Google Identity Services is already loaded
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }

      // Load Google Identity Services
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  };

  const initializeGoogleIdentityAuth = async () => {
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '51925686002-eejsr8utkn98khib5tnfd1he1h54ua3v.apps.googleusercontent.com';
    
    try {
      // Initialize Google Identity Services
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredentialResponse
      });

      // Request access token for Gmail API
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        callback: handleTokenResponse,
      });

      // Start the OAuth flow
      tokenClient.requestAccessToken({
        prompt: 'consent'
      });

    } catch (error) {
      console.error('Google Identity Services error:', error);
      throw new Error(`Gmail authentication failed: ${error.message}`);
    }
  };

  const handleCredentialResponse = (response) => {
    console.log('Credential response received');
    // This handles the ID token response
  };

  const handleTokenResponse = async (response) => {
    if (response.error) {
      throw new Error('Access token error: ' + response.error);
    }

    console.log('✅ Access token received');
    
    // Store the access token
    const accessToken = response.access_token;
    
    // Verify user email
    await verifyUserAndFetchEmails(accessToken);
  };

  const verifyUserAndFetchEmails = async (accessToken) => {
    try {
      // Get user profile to verify email
      const profileResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      const profile = await profileResponse.json();
      
      if (profile.email !== 'wyner@s-chem.com.sg') {
        throw new Error(`Please sign in with wyner@s-chem.com.sg\nCurrently signed in as: ${profile.email}`);
      }

      console.log(`✅ Successfully connected to Gmail: ${profile.email}`);
      
      setIsConnected(true);
      setConnectionStatus('connected');
      setLastSync(new Date());
      
      // Store access token for Gmail API calls
      window.gmailAccessToken = accessToken;
      
      await fetchRealFilteredEmails(accessToken);
    } catch (error) {
      console.error('User verification error:', error);
      throw error;
    }
  };

  const fetchRealFilteredEmails = async (accessToken) => {
    try {
      const query = 'is:unread after:2025/8/1';
      console.log(`📧 Fetching emails with query: ${query}`);

      const response = await fetch(
        `https://www.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}&maxResults=50`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Gmail API error: ${response.status}`);
      }

      const data = await response.json();

      if (!data.messages) {
        console.log('📭 No unread emails found since August 1, 2025');
        setEmails([]);
        return;
      }

      const emailPromises = data.messages.slice(0, 30).map(async (message) => {
        const emailResponse = await fetch(
          `https://www.googleapis.com/gmail/v1/users/me/messages/${message.id}?format=full`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          }
        );
        
        const emailData = await emailResponse.json();
        return formatGmailMessage(emailData);
      });

      const emailDetails = await Promise.all(emailPromises);
      setEmails(emailDetails);
      
      console.log(`✅ Successfully loaded ${emailDetails.length} unread emails`);

    } catch (error) {
      console.error('Error fetching real emails:', error);
      setEmails([]);
      throw error;
    }
  };

  const formatGmailMessage = (message) => {
    const headers = message.payload.headers;
    const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';
    
    let body = '';
    if (message.payload.body && message.payload.body.data) {
      body = atob(message.payload.body.data.replace(/-/g, '+').replace(/_/g, '/'));
    } else if (message.payload.parts) {
      for (const part of message.payload.parts) {
        if (part.mimeType === 'text/plain' && part.body.data) {
          body = atob(part.body.data.replace(/-/g, '+').replace(/_/g, '/'));
          break;
        }
      }
    }
    
    if (!body) {
      body = message.snippet;
    }

    const from = getHeader('From').toLowerCase();
    const isVIP = from.includes('gls.com') || from.includes('ntuc.com');
    
    return {
      id: message.id,
      threadId: message.threadId,
      from: getHeader('From'),
      to: getHeader('To'),
      subject: getHeader('Subject'),
      body: body,
      snippet: message.snippet,
      timestamp: new Date(parseInt(message.internalDate)),
      labels: message.labelIds || [],
      isVIP: isVIP,
      isUnread: message.labelIds?.includes('UNREAD') || true
    };
  };

  const processEmails = async () => {
    if (!isConnected || emails.length === 0) return;
    
    setIsMonitoring(true);
    const processed = [];
    
    for (const email of emails) {
      // Use full AI classification
      const classification = await classifyEmail(email);
      
      const processedEmail = {
        ...email,
        ...classification,
        processedAt: new Date(),
        testMode: testMode,
        executionResults: {}
      };
      
      // EXECUTE AUTOMATIONS (even in test mode for debugging)
      
      // 1. WhatsApp message execution
      if (whatsappConnected && classification.whatsappMessage) {
        console.log(`${testMode ? '[TEST MODE] ' : ''}Sending WhatsApp message...`);
        const whatsappResult = await sendWhatsAppMessage(
          whatsappConfig.assistantNumber, 
          `${testMode ? '[TEST MESSAGE] ' : ''}${classification.whatsappMessage}`
        );
        processedEmail.executionResults.whatsapp = whatsappResult;
        console.log(`WhatsApp result:`, whatsappResult);
      }
      
      // 2. Customer email response execution  
      if (classification.customerResponse && classification.classification === 'PURCHASE_ORDER') {
        console.log(`${testMode ? '[TEST MODE] ' : ''}Would send customer email response`);
        
        // In test mode, we simulate sending but don't actually send
        // In live mode, this would integrate with Gmail Send API
        const emailResult = {
          success: true,
          messageId: `test_email_${Date.now()}`,
          testMode: testMode,
          recipient: email.from,
          subject: `Re: ${email.subject}`,
          body: classification.customerResponse
        };
        
        processedEmail.executionResults.customerEmail = emailResult;
        console.log(`Customer email result:`, emailResult);
      }
      
      // 3. Mark email as read execution
      if (window.gmailAccessToken) {
        console.log(`${testMode ? '[TEST MODE] ' : ''}Marking email as read...`);
        
        if (testMode) {
          // In test mode, simulate marking as read without actually doing it
          const markReadResult = {
            success: true,
            emailId: email.id,
            action: 'simulated_mark_read',
            testMode: true
          };
          processedEmail.executionResults.markRead = markReadResult;
          console.log(`Mark read result (simulated):`, markReadResult);
        } else {
          // In live mode, actually mark as read
          const markReadResult = await markEmailAsRead(email.id);
          processedEmail.executionResults.markRead = markReadResult;
          console.log(`Mark read result:`, markReadResult);
        }
      }
      
      processed.push(processedEmail);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    setProcessedEmails(processed);
    setIsMonitoring(false);
    
    // Summary log for debugging
    console.log('=== PROCESSING SUMMARY ===');
    processed.forEach((email, index) => {
      console.log(`Email ${index + 1}: ${email.subject}`);
      console.log(`  Classification: ${email.classification}`);
      console.log(`  WhatsApp sent: ${!!email.executionResults.whatsapp}`);
      console.log(`  Customer email: ${!!email.executionResults.customerEmail}`);
      console.log(`  Marked read: ${!!email.executionResults.markRead}`);
    });
  };

  const classifyEmail = async (email) => {
    try {
      const response = await fetch("/api/classify-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const classification = await response.json();
      return classification;
    } catch (error) {
      console.error("Classification error:", error);
      return {
        classification: "GENERAL",
        priority: "medium",
        isVIP: false,
        reasoning: "Error in classification - defaulting to manual review",
        customerResponse: null,
        whatsappMessage: null,
        actionRequired: "Manual review required due to classification error",
        confidence: 0.0
      };
    }
  };

  const markEmailAsRead = async (emailId) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/gmail/v1/users/me/messages/${emailId}/modify`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${window.gmailAccessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            removeLabelIds: ['UNREAD']
          })
        }
      );
      
      if (response.ok) {
        console.log(`✅ Marked email ${emailId} as read`);
        return { success: true, emailId: emailId };
      } else {
        console.error(`❌ Failed to mark email ${emailId} as read`);
        return { success: false, emailId: emailId, error: 'API request failed' };
      }
    } catch (error) {
      console.error(`❌ Error marking email ${emailId} as read:`, error);
      return { success: false, emailId: emailId, error: error.message };
    }
  };

  const sendWhatsAppMessage = async (phoneNumber, message) => {
    if (!whatsappConnected || testMode) {
      console.log('📱 WhatsApp message (test mode):', { phoneNumber, message });
      return { success: true, messageId: 'test_msg_' + Date.now() };
    }

    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/${whatsappConfig.phoneNumberId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${whatsappConfig.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messaging_product: 'whatsapp',
            to: phoneNumber.replace(/\D/g, ''),
            text: { body: message },
            type: 'text'
          })
        }
      );

      const data = await response.json();
      if (response.ok) {
        console.log('✅ WhatsApp message sent successfully');
        return { success: true, messageId: data.messages[0].id };
      } else {
        throw new Error(data.error?.message || 'Failed to send WhatsApp message');
      }
    } catch (error) {
      console.error('❌ WhatsApp send error:', error);
      return { success: false, error: error.message };
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
            📧 S-Chem Email Assistant
          </h1>
          <p style={{ color: '#6b7280' }}>
            Production deployment for wyner@s-chem.com.sg
          </p>
        </div>

        {/* Connection Status */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
            Integration Status
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            {/* Gmail Status */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>📧</span>
                <h3 style={{ fontWeight: '600', color: '#111827' }}>Gmail</h3>
                <span style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderRadius: '9999px',
                  backgroundColor: isConnected ? '#dcfce7' : '#f3f4f6',
                  color: isConnected ? '#166534' : '#374151'
                }}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Account:</span>
                  <span style={{ fontWeight: '500' }}>wyner@s-chem.com.sg</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Last Sync:</span>
                  <span style={{ fontWeight: '500' }}>
                    {lastSync ? lastSync.toLocaleTimeString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            {/* WhatsApp Status */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>💬</span>
                <h3 style={{ fontWeight: '600', color: '#111827' }}>WhatsApp Business</h3>
                <span style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderRadius: '9999px',
                  backgroundColor: whatsappConnected ? '#dcfce7' : '#f3f4f6',
                  color: whatsappConnected ? '#166534' : '#374151'
                }}>
                  {whatsappConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Assistant:</span>
                  <span style={{ fontWeight: '500' }}>
                    {whatsappConfig.assistantNumber || 'Not configured'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>API Status:</span>
                  <span style={{ fontWeight: '500' }}>
                    {whatsappConnected ? 'Ready' : 'Setup Required'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            {!isConnected ? (
              <button
                onClick={connectToGmail}
                disabled={connectionStatus === 'connecting'}
                style={{
                  flex: 1,
                  backgroundColor: '#2563eb',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: connectionStatus === 'connecting' ? 'not-allowed' : 'pointer',
                  opacity: connectionStatus === 'connecting' ? 0.5 : 1,
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {connectionStatus === 'connecting' ? 'Connecting to Gmail...' : '🔗 Connect Gmail'}
              </button>
            ) : (
              <button
                onClick={processEmails}
                disabled={isMonitoring || emails.length === 0}
                style={{
                  flex: 1,
                  backgroundColor: '#16a34a',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: (isMonitoring || emails.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (isMonitoring || emails.length === 0) ? 0.5 : 1,
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isMonitoring ? 'Processing Emails...' : '⚡ Process Emails'}
              </button>
            )}

            <button
              onClick={() => setTestMode(!testMode)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: testMode ? '#fef3c7' : '#fee2e2',
                color: testMode ? '#92400e' : '#991b1b',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {testMode ? 'Test Mode' : 'Live Mode'}
            </button>
          </div>
        </div>

        {/* Status Notice */}
        <div style={{
          border: '1px solid ' + (testMode ? '#3b82f6' : '#ef4444'),
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          backgroundColor: testMode ? '#eff6ff' : '#fef2f2'
        }}>
          <h3 style={{
            fontWeight: '600',
            marginBottom: '8px',
            color: testMode ? '#1d4ed8' : '#dc2626'
          }}>
            {testMode ? '🧪 Test Mode - Safe Testing' : '🚨 LIVE MODE - Full Automation Active'}
          </h3>
          <ul style={{ fontSize: '14px', color: testMode ? '#1e40af' : '#b91c1c', margin: 0, paddingLeft: '20px' }}>
            {testMode ? (
              <>
                <li>Connected to your real Gmail account</li>
                <li>Emails will NOT be marked as read</li>
                <li>WhatsApp messages will NOT be sent</li>
                <li>Safe for testing and reviewing AI responses</li>
              </>
            ) : (
              <>
                <li>🚨 LIVE AUTOMATION: Emails will be automatically processed</li>
                <li>📧 Customer emails will be sent automatically</li>
                <li>📱 WhatsApp messages will be sent to your assistant</li>
                <li>✅ Processed emails will be marked as read</li>
              </>
            )}
          </ul>
        </div>

        {/* Email Display */}
        {emails.length > 0 && processedEmails.length === 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
            <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
                Unread Emails from S-Chem Account ({emails.length})
              </h2>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                Gmail query: <code style={{ backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>is:unread after:2025/8/1</code>
              </p>
            </div>
            <div>
              {emails.map((email, index) => (
                <div key={email.id} style={{ padding: '24px', borderBottom: index < emails.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h3 style={{ fontWeight: '600', color: '#111827', margin: 0 }}>{email.subject}</h3>
                        {email.isUnread && (
                          <span style={{
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                            borderRadius: '4px'
                          }}>
                            UNREAD
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>From: {email.from}</p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                        {email.timestamp.toLocaleString()}
                      </p>
                    </div>
                    {email.isVIP && (
                      <span style={{
                        padding: '2px 8px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: '#fecaca',
                        color: '#991b1b',
                        borderRadius: '4px'
                      }}>
                        VIP
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '16px',
                    borderRadius: '8px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit'
                  }}>
                    {email.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Processed Results */}
        {processedEmails.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
                Email Processing Results
              </h2>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                AI analysis and automated actions ({testMode ? 'test mode' : 'live mode'})
              </p>
            </div>
            <div>
              {processedEmails.map((email, index) => (
                <div key={email.id} style={{ padding: '24px', borderBottom: index < processedEmails.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          borderRadius: '9999px',
                          border: '1px solid',
                          backgroundColor: email.classification === 'VIP_URGENT' ? '#fef2f2' : '#f3f4f6',
                          color: email.classification === 'VIP_URGENT' ? '#991b1b' : '#374151',
                          borderColor: email.classification === 'VIP_URGENT' ? '#fecaca' : '#d1d5db'
                        }}>
                          {email.classification.replace('_', ' ')}
                        </span>
                        {email.isVIP && (
                          <span style={{
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            borderRadius: '4px'
                          }}>
                            VIP
                          </span>
                        )}
                      </div>
                      <h3 style={{ fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>{email.subject}</h3>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>From: {email.from}</p>
                    </div>
                  </div>
                  
                  <div style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{ fontWeight: '500', color: '#111827', margin: '0 0 8px 0' }}>AI Analysis:</h4>
                    <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>{email.reasoning}</p>
                  </div>

                  {email.executionResults && (
                    <div style={{
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      padding: '16px',
                      marginTop: '16px'
                    }}>
                      <h4 style={{ fontWeight: '600', color: '#0c4a6e', margin: '0 0 12px 0' }}>
                        🔧 Execution Results (Debug Info):
                      </h4>
                      
                      {email.executionResults.whatsapp && (
                        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                          <strong>WhatsApp:</strong>{' '}
                          <span style={{ color: email.executionResults.whatsapp.success ? '#059669' : '#dc2626' }}>
                            {email.executionResults.whatsapp.success ? '✅ Sent' : '❌ Failed'}
                          </span>
                          {email.executionResults.whatsapp.messageId && (
                            <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>
                              ID: {email.executionResults.whatsapp.messageId}
                            </span>
                          )}
                          {email.executionResults.whatsapp.error && (
                            <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                              Error: {email.executionResults.whatsapp.error}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {email.executionResults.customerEmail && (
                        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                          <strong>Customer Email:</strong>{' '}
                          <span style={{ color: email.executionResults.customerEmail.success ? '#059669' : '#dc2626' }}>
                            {email.executionResults.customerEmail.success ? '✅ Sent' : '❌ Failed'}
                          </span>
                          <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px' }}>
                            To: {email.executionResults.customerEmail.recipient}
                          </span>
                          {email.executionResults.customerEmail.testMode && (
                            <span style={{ color: '#f59e0b', fontSize: '12px', marginLeft: '8px' }}>
                              (Simulated)
                            </span>
                          )}
                        </div>
                      )}
                      
                      {email.executionResults.markRead && (
                        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                          <strong>Mark as Read:</strong>{' '}
                          <span style={{ color: email.executionResults.markRead.success ? '#059669' : '#dc2626' }}>
                            {email.executionResults.markRead.success ? '✅ Done' : '❌ Failed'}
                          </span>
                          {email.executionResults.markRead.testMode && (
                            <span style={{ color: '#f59e0b', fontSize: '12px', marginLeft: '8px' }}>
                              (Simulated)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Not Connected State */}
        {!isConnected && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
              Connect Your S-Chem Gmail Account
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Click "Connect Gmail" to access your real wyner@s-chem.com.sg emails and start automation.
            </p>
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'left'
            }}>
              <h4 style={{ fontWeight: '500', color: '#166534', margin: '0 0 8px 0' }}>Production Ready Features:</h4>
              <ul style={{ fontSize: '14px', color: '#166534', margin: 0, paddingLeft: '20px' }}>
                <li>✅ Real Gmail integration with OAuth2</li>
                <li>✅ WhatsApp Business API connected</li>
                <li>✅ AI email classification and responses</li>
                <li>✅ VIP client detection (GLS, NTUC)</li>
                <li>✅ Automated email processing</li>
                <li>✅ Test mode for safe experimentation</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailAssistant;
